import { AutoComplete, Input, Spin } from 'antd';
import React, { useMemo } from 'react';
import { IoMdCloseCircle } from 'react-icons/io';
import { useSelector } from 'react-redux';
import {
  AUTOCOMPLETE_TYPE,
  removeDuplicateAutocomplete,
} from '@skygroup/shared/API/autoCompleteApi/autoCompleteUtils';
import {
  useGetCityAutoComplete,
  useGetHotelAutoComplete,
} from '@skygroup/shared/API/autoCompleteApi/autoCompleteApi';
import { RootState } from '../../../../../../store/store';
import { CityElement } from '../../../CustomAutoComplete/CustomAutoCompleteCity';
import { HotelElement } from '../../../CustomAutoComplete/HotelElement';
import '../../../CustomAutoComplete/customAutoComplete.less';
import './hotel-search-input.less';
import { GEO_HOTEL_RADIUS } from '../hooks/useHotelDataManagementByGeo';

const API_LIMIT = 50;

interface HotelSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  isCompact?: boolean;
  searchedResultsLength?: number;
  includeAutocompleteAPI?: boolean;
  includeCityResults?: boolean;
  coords?: {
    latitude: number;
    longitude: number;
  };
  onCitySelect?: (city: { location_id: string; name: string }) => void;
  onHotelSelect?: (hotel: { identifier: string; name: string }) => void;
}

type HotelRow = {
  address?: string;
  country?: { name: string };
  identifier: string;
  image?: string[];
  name: string;
  stars?: number;
  type?: 'Hotel';
  value?: string;
  distance?: number;
};

interface HotelAutocompleteOption {
  value: string;
  hotelName: string;
  label: React.ReactNode;
  distance?: number;
}

function normalizeCityName(city: {
  name?: string;
  country?: string | { name?: string };
}) {
  const country =
    typeof city.country === 'string' ? city.country : city.country?.name ?? '';
  return {
    name: city.name ?? '',
    country,
  };
}

interface CityAutocompleteOption {
  value: string;
  cityName: string;
  label: React.ReactNode;
  distance?: number;
}

function normalizeHotelForElement(hotel: HotelRow) {
  return {
    ...hotel,
    type: 'Hotel' as const,
    address: hotel.address ?? '',
    country: hotel.country ?? { name: '' },
    image: hotel.image ?? [],
    stars: hotel.stars ?? 0,
    name: hotel.name ?? '',
    identifier: String(hotel.identifier ?? hotel.value ?? ''),
  };
}

function isFavoriteHotelEntry(option: any): boolean {
  if (option?.type === 'Hotel') {
    return true;
  }
  // Exclude airport/city favorites that carry iata_code without hotel identifier
  if (option?.iata_code && !option?.identifier) {
    return false;
  }
  return !!(option?.identifier && option?.name);
}

const HotelSearchInput: React.FC<HotelSearchInputProps> = ({
  value,
  onChange,
  isCompact = false,
  searchedResultsLength,
  includeAutocompleteAPI = false,
  includeCityResults = false,
  coords,
  onCitySelect,
  onHotelSelect,
}) => {
  const { general: general_t, searchDatePicker: t } = useSelector(
    (data: RootState) => data.config.translations
  );
  const { currentLang } = useSelector((data: RootState) => data.config);
  const favorite_locations = useSelector(
    (state: RootState) =>
      state.config.config.siteConfig.searchConfig?.hotels?.favorite_locations
  );

  const apiEnabled = includeAutocompleteAPI && (value?.length ?? 0) > 1;

  const cityApiEnabled = apiEnabled && includeCityResults;

  const { data: cityData, isFetching: isCityDataFetching } =
    useGetCityAutoComplete(value, currentLang, cityApiEnabled, coords);

  const { data: hotelData, isFetching: isHotelDataFetching } =
    useGetHotelAutoComplete({
      searchVal: value,
      currentLang,
      enable: apiEnabled,
      coords,
      limit: API_LIMIT,
    });

  const favoriteHotelOptions = useMemo(() => {
    if (!includeAutocompleteAPI || !favorite_locations?.length) {
      return [];
    }
    return favorite_locations
      .filter(isFavoriteHotelEntry)
      .map((option: HotelRow & { value?: string }) => {
        const h = normalizeHotelForElement(option);
        const hotelObject: HotelAutocompleteOption = {
          value: h.identifier,
          hotelName: h.name,
          label: <HotelElement {...h} />,
        };
        if (h.distance) {
          hotelObject.distance = h.distance;
        }
        return hotelObject;
      });
  }, [includeAutocompleteAPI, favorite_locations]);

  const apiHotelOptions = useMemo(() => {
    if (!hotelData?.data?.hotel?.length) {
      return [];
    }
    return removeDuplicateAutocomplete(
      hotelData.data.hotel,
      AUTOCOMPLETE_TYPE.HOTEL_IDENTIFIER
    ).map((hotel: HotelRow) => {
      const h = normalizeHotelForElement(hotel);
      const hotelObject: HotelAutocompleteOption = {
        value: h.identifier,
        hotelName: h.name,
        label: <HotelElement {...h} />,
      };
      if (h.distance) {
        hotelObject.distance = h.distance;
      }
      return hotelObject;
    });
  }, [hotelData]);

  const apiCityOptions = useMemo(() => {
    if (!includeCityResults || !cityData?.data?.length) {
      return [];
    }
    return removeDuplicateAutocomplete(
      cityData.data,
      AUTOCOMPLETE_TYPE.CITY
    ).map(
      (city: {
        location_id: string;
        name?: string;
        country?: string | { name?: string };
        distance?: number;
      }) => {
        const { name, country } = normalizeCityName(city);
        const cityObject: CityAutocompleteOption = {
          value: String(city.location_id ?? ''),
          cityName: name,
          label: (
            <CityElement
              name={name}
              country={country}
              distance={city.distance}
            />
          ),
        };
        if (city.distance != null) {
          cityObject.distance = city.distance;
        }
        return cityObject;
      }
    );
  }, [cityData, includeCityResults]);

  const showFavoriteGroup = (value?.trim().length ?? 0) <= 1;

  const autocompleteOptions = useMemo(() => {
    const groups: {
      label: string;
      options: (HotelAutocompleteOption | CityAutocompleteOption)[];
    }[] = [];
    if (showFavoriteGroup && favoriteHotelOptions.length > 0) {
      groups.push({ label: t.hotel, options: favoriteHotelOptions });
    }
    const filteredApiHotelOptions = apiHotelOptions.filter(
      (hotel) => !hotel?.distance || hotel.distance <= GEO_HOTEL_RADIUS
    );
    const filteredApiCityOptions = apiCityOptions.filter(
      (city) => !city?.distance || city.distance <= GEO_HOTEL_RADIUS
    );
    if (!showFavoriteGroup) {
      if (includeCityResults && filteredApiCityOptions.length > 0) {
        groups.push({ label: t.city, options: filteredApiCityOptions });
      }
      if (filteredApiHotelOptions.length > 0) {
        groups.push({ label: t.hotel, options: filteredApiHotelOptions });
      }
    }
    return groups;
  }, [
    showFavoriteGroup,
    favoriteHotelOptions,
    apiHotelOptions,
    apiCityOptions,
    includeCityResults,
    t.hotel,
    t.city,
  ]);

  const showDropdownLoading =
    includeAutocompleteAPI &&
    !showFavoriteGroup &&
    (isHotelDataFetching || (includeCityResults && isCityDataFetching));

  const resultsBlock = searchedResultsLength ? (
    <b className="results-length">
      {general_t.matched} {t.hotel}: {searchedResultsLength}
    </b>
  ) : null;

  if (!includeAutocompleteAPI) {
    return (
      <div
        className={`search-hotel-name-wrapper ${isCompact ? 'isCompact' : ''}`}
      >
        <label className="label-name">{t.searchHotelsByNameTitle}</label>
        <Input
          className="input"
          value={value}
          onChange={(e) => onChange(e?.target?.value)}
          placeholder={isCompact ? t.searchHotelsByNameTitle : general_t.search}
          allowClear
        />
        {resultsBlock}
      </div>
    );
  }

  return (
    <div
      className={`search-hotel-name-wrapper search-hotel-name-wrapper--autocomplete ${
        isCompact ? 'isCompact' : ''
      }`}
    >
      <label className="label-name">
        {t.searchHotelsByNameTitle}
        {/* {showDropdownLoading ? (
          <Spin className="hotel-search-input-spin" size="small" />
        ) : null} */}
      </label>
      <AutoComplete
        className="input hotel-search-autocomplete"
        value={value}
        options={autocompleteOptions}
        filterOption={false}
        allowClear
        clearIcon={<IoMdCloseCircle className="close-icon" size={20} />}
        popupClassName={`custom-auto-complete-dropdown`}
        dropdownMatchSelectWidth={320}
        placeholder={isCompact ? t.searchHotelsByNameTitle : general_t.search}
        notFoundContent={
          showDropdownLoading ? (
            <Spin size="default" style={{ margin: 'auto', width: '100%' }} />
          ) : undefined
        }
        onChange={(v) => onChange(String(v ?? ''))}
        onSelect={(_selectedValue, option) => {
          const opt = option as unknown as Partial<
            HotelAutocompleteOption & CityAutocompleteOption
          >;
          if (opt.hotelName) {
            onChange(opt.hotelName);
            onHotelSelect?.({ identifier: _selectedValue, name: opt.hotelName });
          } else if (opt.cityName) {
            onChange(opt.cityName);
            onCitySelect?.({ location_id: _selectedValue, name: opt.cityName });
          }
        }}
      />
      {resultsBlock}
      <span style={{ fontSize: 14, fontWeight: 500, color: '#999', marginTop: 5 }}>
        * {t.showingAllResultsWithin} {GEO_HOTEL_RADIUS} km
      </span>
    </div>
  );
};

export default HotelSearchInput;
